import React, { useEffect, useState } from "react";
import { Head, MainContent, ButtonGroup } from "./styled";
import Fieldset from "_c/form/Fieldset";
import Label from "_c/form/Label";
import { Map, DraggableMarker } from "_c/modules/geo/Map";
import { useFormContext } from "react-hook-form";
import {
  PlacesAutocompleteField,
  AbstractControlledField,
} from "_c/form/Field";
import s from "styled-components";
import { CircleButton, Button } from "_c/button";
import PinIcon from "_c/icon/Pin";
import EyeIcon from "_c/icon/EyeOpen";
import EyeClosedIcon from "_c/icon/EyeClosed";
import { useNotify } from "@/hooks/useNotify";
import { useToggle } from "@/hooks/useToggle";
import { GeoService } from "@/services/geo";
import { useForm, FormContext } from "react-hook-form";
import { ns as baseNs } from "./config";
import { useMut } from "@/hooks/useMut";
import { Effects as UserEffects } from "@/store/user/effects";
import { Selectors as UserSelectors } from "@/store/user/selectors";
import { useSelector, useDispatch } from "react-redux";
import isempty from "lodash/isEmpty";
import { analyticsAction } from "@/utils/analytics";
import AfterStepInfo from "./AfterStepInfo";
import { useHistory } from "react-router-dom";
const ns = baseNs + "step_location.";

const Tip = s.div`
  span {
    color: var(--font-color-emp);
  }
  ${CircleButton} {
    display: inline-flex;
    margin-right: 5px;
    color: white;
  }
  margin-bottom: 5px;
`;
const Buttons = s.div`
  margin: 10px 0;
  button {
    margin-right: 5px;
    margin-bottom: 5px;
  }
`;

const MapContainer = s.div`
  width: 100%;
  height: 400px;
`;

const afterStepImageUrl = "/images/itsdone.jpg";
const formId = "master-initial-setup-location-form";
const StepAddress = ({ t, goNext }) => {
  useEffect(() => {
    analyticsAction({
      category: "wizard",
      action: "address_screen",
    });
  }, []);
  const history = useHistory();
  const [shouldShowAfterStepInfo, setShouldShowAfterStepInfo] = useState(false);
  const showAfterStepInfo = () => setShouldShowAfterStepInfo(true);
  const dispatch = useDispatch();
  const mut = useMut((form) => dispatch(UserEffects.updateMaster(form)), {
    t,
    // success or not - go next anyway
    onSuccess: showAfterStepInfo,
    onError: showAfterStepInfo,
  });
  const master = useSelector(UserSelectors.getMaster);
  const formMethods = useForm({
    defaultValues: {
      location: master?.location?.coordinates ?? [0, 0],
      location_hr: master?.location_hr ?? "",
    },
  });
  const { handleSubmit, watch } = formMethods;
  const { location_hr: textLoc, location: loc } = watch();
  const canSubmit = Boolean(textLoc && loc?.some((c) => c !== 0));
  const onSubmit = ({ location_hr, location }) => {
    const norm = {
      location_hr: location_hr || "",
      location:
        isempty(location) || !location_hr
          ? null
          : {
              type: "Point",
              coordinates: location,
            },
    };
    analyticsAction({
      category: "master_setup_wizard",
      action: "found_address_button",
    });
    mut.mutate(norm);
  };
  if (shouldShowAfterStepInfo) {
    return (
      <AfterStepInfo
        img={afterStepImageUrl}
        analytics="booking_ready_screen"
        buttons={[
          {
            text: t("common:next"),
            onClick: () => {
              history.push("/app/dashboard");
              goNext();
            },
            props: { greenish: true },
          },
        ]}
        text={t(ns + "post_step.text")}
        header={t(ns + "post_step.header")}
      />
    );
  }
  return (
    <>
      <Head>{t(ns + "title")}</Head>
      <MainContent as="form" id={formId} onSubmit={handleSubmit(onSubmit)}>
        <FormContext {...formMethods}>
          <Location t={t} />
        </FormContext>
      </MainContent>
      <ButtonGroup>
        <Button
          small
          negative
          onClick={() => {
            analyticsAction({
              category: "master_setup_wizard",
              action: "notfound_address_button",
            });
            showAfterStepInfo();
          }}
        >
          {t(ns + "not_found")}
        </Button>
        <Button
          disabled={mut.isLoading || !canSubmit}
          form={formId}
          small
          greenish
          type="submit"
        >
          {t(ns + "found")}
        </Button>
      </ButtonGroup>
    </>
  );
};

const MapField = ({ saloon, t, ...props }) => {
  const { setValue } = useFormContext();
  const { notifyError } = useNotify();
  const [showMap, toggleShowMap] = useToggle(false);
  const locale = useSelector(UserSelectors.getLocale);
  const onDragEnd = async ({ lat, lng }) => {
    props.onChange([lng, lat]);
    const data = await GeoService.reverseGeocode({ lat, lng });
    if (data) {
      setValue("location_hr", data.name || data.city);
    }
  };
  const onFind = async () => {
    try {
      analyticsAction({
        category: "wizard",
        action: "address_find_me",
      });
      const { name, lat, lng, city } = await GeoService.detectCurrentLocation();
      props.onChange([lng, lat]);
      setValue("location_hr", name || city);
    } catch (e) {
      notifyError(t("errors:geolocation_failed"));
    }
  };
  return (
    <div>
      <Buttons>
        <Button
          style={{ marginRight: "5px" }}
          type="button"
          onClick={onFind}
          small
          withIcon
          greenish
        >
          <PinIcon color="white" />
          <span>{t("geo_find_me_short")}</span>
        </Button>
        <Button type="button" withIcon small onClick={toggleShowMap}>
          {showMap ? (
            <>
              <EyeClosedIcon width="18" color="white" />
              <span>{t("common:hide_map")}</span>
            </>
          ) : (
            <>
              <EyeIcon width="18" color="white" />
              <span>{t("common:show_map")}</span>
            </>
          )}
        </Button>
      </Buttons>
      {showMap && (
        <>
          <Tip>
            <CircleButton as="span" small>
              i
            </CircleButton>
            <span>{t("common:drag_geo_marker_tip")}</span>
          </Tip>
          <div
            css={`
              min-height: 400px;
            `}
          >
            <MapContainer>
              <Map locale={locale} center={props.value}>
                <DraggableMarker onDragEnd={onDragEnd} coords={props.value} />
              </Map>
            </MapContainer>
          </div>
        </>
      )}
    </div>
  );
};
const Location = ({ t }) => {
  const { setValue } = useFormContext();
  const onPlaceSelect = async (loc) => {
    setValue("location_hr", loc.name || loc.fullName);
    setValue("location", [loc.lng, loc.lat]);
  };

  return (
    <Fieldset
      css={`
        border: none;
        margin: 0;
        padding: 0;
      `}
    >
      <Label as="legend">{t(`common:address`)}</Label>
      <PlacesAutocompleteField
        onSelect={onPlaceSelect}
        t={t}
        label={null}
        name="location_hr"
        id="location_hr"
      />
      <AbstractControlledField
        t={t}
        id="location"
        Component={MapField}
        name="location"
      />
    </Fieldset>
  );
};

export default StepAddress;
